import * as React from 'react'

import greenOrb from '@ps/ds/assets/misc/green-orb.svg'
import { Item, ItemContent, ItemDescription, ItemTitle } from '@ps/ds/components/blocks/Item/Item'
import { CircleCheck } from '@ps/ds/icons/CircleCheck'
import { cn } from '@ps/ds/lib/utils'

export interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: StepperStep[]
  /** Zero-based index of the currently active step. */
  activeIndex: number
  disabled?: boolean
}

export interface StepperStep {
  label: string
  description?: React.ReactNode
  icon?: React.ReactNode
}

/** Visual state of a single step, derived from its position relative to `activeIndex`. */
type StepState = 'completed' | 'current' | 'future'

/** Visual style of a connector line segment between step indicators. */
type ConnectorVariant = 'solid' | 'dashed'

function getStepState(index: number, activeIndex: number): StepState {
  if (index < activeIndex) return 'completed'
  if (index === activeIndex) return 'current'
  return 'future'
}

function getConnectorVariant(state: StepState): ConnectorVariant {
  return state === 'completed' ? 'solid' : 'dashed'
}

interface ConnectorLineProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: ConnectorVariant
  /** When true, renders as a fixed 12px stub (used above the indicator). Defaults to a flex-1 segment. */
  stub?: boolean
  disabled?: boolean
}

/** Vertical line segment connecting step indicators. Renders as solid, muted, or dashed. */
function ConnectorLine({
  variant,
  stub = false,
  disabled = false,
  className,
  ...props
}: ConnectorLineProps) {
  return (
    <div
      className={cn(
        stub ? 'h-3 w-[1.75px]' : 'flex-1',
        variant === 'solid' && (disabled ? 'bg-neutral-400' : 'bg-emerald-500'),
        variant === 'dashed' && [
          'bg-[image:linear-gradient(to_bottom,var(--color-navy-500-12)_50%,transparent_50%)]',
          'bg-[size:1.75px_8px]',
          'bg-repeat-y',
        ],
        className,
      )}
      {...props}
    />
  )
}

interface StepIndicatorProps extends React.HTMLAttributes<HTMLDivElement> {
  state: StepState
  icon?: React.ReactNode
  disabled?: boolean
}

/** Circular badge shown to the left of each step's label. */
function StepIndicator({ state, icon, disabled = false, className, ...props }: StepIndicatorProps) {
  const showCheck = state === 'completed' || state === 'current'
  const isActive = !disabled && showCheck

  return (
    <div
      className={cn(
        'relative flex size-10 shrink-0 items-center justify-center rounded-full',
        disabled && showCheck && 'bg-neutral-400',
        !showCheck && 'border-[1.75px] border-navy-500-12 bg-background',
        className,
      )}
      {...props}>
      {isActive && (
        <img src={greenOrb} alt="" aria-hidden="true" className="absolute inset-0 size-full" />
      )}
      {showCheck && (
        <CircleCheck
          className={cn(
            'relative z-10 size-5 shrink-0',
            isActive ? '[&_path]:fill-emerald-500' : '[&_path]:fill-neutral-400',
          )}
          fill="white"
          strokeWidth={2.5}
          aria-hidden="true"
        />
      )}
      {!showCheck && icon != null && (
        <span
          className={cn('flex items-center justify-center [&>svg]:size-5 text-navy-500-30')}
          aria-hidden="true">
          {icon}
        </span>
      )}
    </div>
  )
}

/** Vertical step-progress indicator showing completed, current, and upcoming steps. */
export function Stepper({
  steps,
  activeIndex,
  disabled = false,
  className,
  ...props
}: StepperProps) {
  const clampedIndex = Number.isNaN(activeIndex)
    ? 0
    : Math.min(Math.max(Math.trunc(activeIndex), 0), steps.length)

  return (
    <div {...props} className={cn('flex flex-col', className)} role="list" data-testid="stepper">
      {steps.map((step, index) => {
        const state = getStepState(index, clampedIndex)

        return (
          <div
            key={`${step.label}-${index}`}
            className="flex items-start gap-2"
            role="listitem"
            aria-current={!disabled && index === clampedIndex ? 'step' : undefined}
            data-testid={`step-${index}`}>
            <div className="flex w-10 shrink-0 flex-col items-center self-stretch">
              <div className="flex h-3 w-full shrink-0 justify-center">
                {index > 0 && (
                  <ConnectorLine
                    stub
                    variant={getConnectorVariant(getStepState(index - 1, clampedIndex))}
                    disabled={disabled}
                    data-testid={`step-top-connector-${index}`}
                  />
                )}
              </div>

              <StepIndicator
                state={state}
                icon={step.icon}
                disabled={disabled}
                data-testid={`step-indicator-${index}`}
                data-state={disabled ? `disabled-${state}` : state}
              />

              {index !== steps.length - 1 &&
                (index === clampedIndex ? (
                  // Drip effect: solid top half, dashed bottom half
                  <div
                    className="flex w-[1.75px] min-h-4 flex-1 flex-col"
                    data-testid={`step-bottom-connector-${index}`}>
                    <ConnectorLine variant="solid" disabled={disabled} />
                    <ConnectorLine variant="dashed" />
                  </div>
                ) : (
                  <ConnectorLine
                    className="w-[1.75px] min-h-4"
                    variant={getConnectorVariant(state)}
                    disabled={disabled}
                    data-testid={`step-bottom-connector-${index}`}
                  />
                ))}
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <Item
                className={cn('w-full', !disabled && state === 'current' && 'bg-emerald-200')}
                data-testid={`step-content-${index}`}>
                <ItemContent>
                  <ItemTitle className="leading-none">{step.label}</ItemTitle>
                  {step.description != null && (
                    <ItemDescription className="text-foreground line-clamp-none">
                      {step.description}
                    </ItemDescription>
                  )}
                </ItemContent>
              </Item>
            </div>
          </div>
        )
      })}
    </div>
  )
}
